'use client';

import { useQuery } from '@tanstack/react-query';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { OrderComplete } from '@/features/orders/domain/model/order';
import {
  pickDefaultPrinter,
  pickKitchenPrinter,
  type PrinterReachability,
} from '@/features/printing/domain/service/pick-default-printer';
import {
  applyChannelState,
  type ChannelState,
  type ChannelStates,
  type ChannelTargets,
  INITIAL_CHANNEL_STATES,
  isPrintingChannels,
  parseChannelAssignments,
  parsePrintingMetadata,
  type PartialChannelAssignments,
  PRINT_CHANNELS,
  type PrintablePrinter,
  type PrintChannel,
  type PrintingMetadata,
  resolveChannelTargets,
  serializeChannelAssignments,
  shouldRunChannel,
  toPersistedChannels,
} from '@/features/printing/domain/service/print-channels';
import {
  type PrinterServiceStatus,
  usePrintersService,
} from '@/features/printing/presentation/hooks/use-printers-service';
import { printersQueryOptions } from '@/features/printing/presentation/queries/printers.queries';
import {
  fetchAfipData,
  fetchExistingAfipOrderData,
  hasAfipIntegration,
  printFiscalTicket,
  printKitchenTicket,
  printUserTicket,
  probePrinters,
} from '@/features/printing/presentation/utils/print-tickets';
import { useDashboard } from '@/features/shell/presentation/providers/dashboard-provider';

/** Asignación por canal de esta máquina (localStorage). */
export const ASSIGNMENTS_STORAGE_KEY = 'orderfast_printer_assignments';
/** Clave legada de la selección única de caja; se migra a `caja` la primera vez. */
export const LEGACY_SELECTED_PRINTER_STORAGE_KEY = 'orderfast_dashboard_printer';
export const CUSTOM_PRINTER_STORAGE_KEY = 'orderfast_custom_printer';

const CUSTOM_PRINTER_ID = 'custom-manual-printer';

/** Espera antes del reintento automático de un canal que falló. */
const AUTO_RETRY_DELAY_MS = 800;

export type ProbeState = 'idle' | 'done' | 'unsupported';

export interface PrintingState {
  serviceStatus: PrinterServiceStatus | undefined;
  printers: PrintablePrinter[];
  /** Asignaciones explícitas de esta máquina (ids de impresora por canal). */
  assignments: PartialChannelAssignments;
  /** Impresora resuelta por canal (asignación o fallback por detección). */
  channelTargets: ChannelTargets;
  /** Orden sobre la que corren los estados locales (`channelStates`); `null` si todavía no se imprimió nada. */
  activeOrderId: string | null;
  channelStates: ChannelStates;
  isPrinting: boolean;
  reachability: Record<string, PrinterReachability>;
  isProbing: boolean;
  probeState: ProbeState;
  hasReachablePrinter: boolean;
  isLoadingPrinters: boolean;
}

export interface PrintOrderOptions {
  channels?: PrintChannel[];
}

export interface PrintingActions {
  setChannelPrinter: (channel: PrintChannel, printerId: string | null) => void;
  saveCustomPrinter: (ip: string, port: number, name?: string) => void;
  probeNetwork: () => Promise<void>;
  refreshService: () => void;
  printOrder: (order: OrderComplete, options?: PrintOrderOptions) => Promise<void>;
  retryChannel: (order: OrderComplete, channel: PrintChannel) => Promise<void>;
  printAll: (order: OrderComplete) => Promise<void>;
}

export type OnChannelsSettled = (order: OrderComplete, channels: PrintingMetadata) => void | Promise<void>;

const PrintingStateCtx = createContext<PrintingState | null>(null);
const PrintingActionsCtx = createContext<PrintingActions | null>(null);

function toPrinterInfo(printer: PrintablePrinter): { ip: string; port: number; type: 'cashier' | 'kitchen' } {
  return {
    ip: printer.ip,
    port: printer.port,
    type: printer.type === 'kitchen' ? 'kitchen' : 'cashier',
  };
}

function errorMessage(error: unknown): string {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return 'El servicio de impresión no respondió a tiempo';
  }
  return error instanceof Error ? error.message : 'Error de impresión';
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function usePrintingState(): PrintingState {
  const value = useContext(PrintingStateCtx);
  if (!value) throw new Error('usePrintingState debe usarse dentro de PrintingProvider');
  return value;
}

export function usePrintingActions(): PrintingActions {
  const value = useContext(PrintingActionsCtx);
  if (!value) throw new Error('usePrintingActions debe usarse dentro de PrintingProvider');
  return value;
}

/** Variantes opcionales para componentes que también se renderizan fuera del provider (páginas públicas). */
export function useOptionalPrintingState(): PrintingState | null {
  return useContext(PrintingStateCtx);
}

export function useOptionalPrintingActions(): PrintingActions | null {
  return useContext(PrintingActionsCtx);
}

interface PrintingProviderProps {
  children: ReactNode;
  /** Persiste el resultado de cada tanda (ej. `orders.metadata.printing`). Opcional. */
  onChannelsSettled?: OnChannelsSettled;
}

export function PrintingProvider({ children, onChannelsSettled }: PrintingProviderProps) {
  const { location } = useDashboard();
  const locationId = location?.id;
  const { data: serviceStatus, refetch: refetchService } = usePrintersService();
  const printersQuery = useQuery(printersQueryOptions(locationId));

  const [customPrinter, setCustomPrinter] = useState<PrintablePrinter | null>(null);
  const [assignments, setAssignments] = useState<PartialChannelAssignments>({});
  const [activeOrderId, setActiveOrderId] = useState<string | null>(null);
  const [channelStates, setChannelStates] = useState<ChannelStates>(INITIAL_CHANNEL_STATES);
  const [reachability, setReachability] = useState<Record<string, PrinterReachability>>({});
  const [isProbing, setIsProbing] = useState(false);
  const [isBatchRunning, setIsBatchRunning] = useState(false);
  const [probeState, setProbeState] = useState<ProbeState>('idle');

  const probedSignatureRef = useRef<string | null>(null);
  const onSettledRef = useRef<OnChannelsSettled | undefined>(onChannelsSettled);
  /** Cola de envíos: nunca hay dos tandas de impresión en paralelo. */
  const batchQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  /** Orden activa en la cola (se actualiza de forma sincrónica para las tandas encoladas). */
  const activeOrderIdRef = useRef<string | null>(null);
  /** Estado de canales en la cola (ref sincrónico, además del state que consume la UI). */
  const channelStatesRef = useRef<ChannelStates>(INITIAL_CHANNEL_STATES);

  useEffect(() => {
    onSettledRef.current = onChannelsSettled;
  }, [onChannelsSettled]);

  const serverPrinters = useMemo<PrintablePrinter[]>(
    () =>
      (printersQuery.data ?? []).map(printer => ({
        id: printer.id,
        name: printer.name,
        ip: printer.ip,
        port: printer.port,
        type: printer.type,
      })),
    [printersQuery.data]
  );

  const printers = useMemo<PrintablePrinter[]>(() => {
    if (!customPrinter) return serverPrinters;
    return [customPrinter, ...serverPrinters.filter(printer => printer.id !== customPrinter.id)];
  }, [customPrinter, serverPrinters]);

  const reachableIps = useMemo(
    () =>
      Object.entries(reachability)
        .filter(([, value]) => value === 'reachable')
        .map(([ip]) => ip),
    [reachability]
  );

  const hostIp = serviceStatus?.hostIp ?? null;

  // Fallbacks por canal cuando no hay asignación explícita en esta máquina.
  const cajaFallback = useMemo(
    () =>
      pickDefaultPrinter(printers, {
        reachableIps: probeState === 'done' ? reachableIps : null,
        hostIp,
      }).printer,
    [printers, probeState, reachableIps, hostIp]
  );

  const cocinaFallback = useMemo(
    () => pickKitchenPrinter(printers, { reachableIps: probeState === 'done' ? reachableIps : null }),
    [printers, probeState, reachableIps]
  );

  const channelTargets = useMemo(
    () => resolveChannelTargets(printers, assignments, { caja: cajaFallback, cocina: cocinaFallback }),
    [printers, assignments, cajaFallback, cocinaFallback]
  );

  const updateChannel = useCallback((channel: PrintChannel, state: ChannelState) => {
    channelStatesRef.current = applyChannelState(channelStatesRef.current, channel, state);
    setChannelStates(channelStatesRef.current);
  }, []);

  // Leer las asignaciones persistidas en esta máquina (una por canal).
  useEffect(() => {
    const rawAssignments = localStorage.getItem(ASSIGNMENTS_STORAGE_KEY);
    const legacySelected = localStorage.getItem(LEGACY_SELECTED_PRINTER_STORAGE_KEY);
    const storedCustom = localStorage.getItem(CUSTOM_PRINTER_STORAGE_KEY);

    if (storedCustom) {
      try {
        const parsed = JSON.parse(storedCustom) as PrintablePrinter;
        if (parsed?.ip && parsed?.port) {
          // eslint-disable-next-line react-hooks/set-state-in-effect
          setCustomPrinter({ ...parsed, id: CUSTOM_PRINTER_ID, type: parsed.type ?? 'cashier' });
        }
      } catch {
        // localStorage corrupto: se ignora y se usa la lista del local.
      }
    }

    let parsedAssignments = parseChannelAssignments(rawAssignments);

    // Migración suave: la selección única de caja pasa a ser la asignación del canal caja.
    if (!rawAssignments && legacySelected) {
      parsedAssignments = { ...parsedAssignments, caja: legacySelected };
      localStorage.setItem(ASSIGNMENTS_STORAGE_KEY, serializeChannelAssignments(parsedAssignments));
    }

    setAssignments(parsedAssignments);
  }, []);

  const runProbe = useCallback(async (targets: PrintablePrinter[]) => {
    if (targets.length === 0) return;

    setIsProbing(true);
    setReachability(previous => {
      const next: Record<string, PrinterReachability> = { ...previous };
      for (const printer of targets) next[printer.ip] = 'checking';
      return next;
    });

    try {
      const results = await probePrinters(targets.map(printer => ({ ip: printer.ip, port: printer.port })));

      if (results === null) {
        setReachability(previous => {
          const next: Record<string, PrinterReachability> = { ...previous };
          for (const printer of targets) next[printer.ip] = 'unknown';
          return next;
        });
        setProbeState('unsupported');
        return;
      }

      const reachable = new Set(results.filter(result => result.reachable).map(result => result.ip));

      setReachability(previous => {
        const next: Record<string, PrinterReachability> = { ...previous };
        for (const printer of targets) next[printer.ip] = reachable.has(printer.ip) ? 'reachable' : 'unreachable';
        return next;
      });
      setProbeState('done');
    } finally {
      setIsProbing(false);
    }
  }, []);

  const probeNetwork = useCallback(async () => {
    await runProbe(printers);
  }, [runProbe, printers]);

  // Probe automático al cargar la lista: una vez por firma de impresoras (ip:puerto).
  const printersSignature = useMemo(
    () => printers.map(printer => `${printer.ip}:${printer.port}`).join('|'),
    [printers]
  );

  useEffect(() => {
    if (!serviceStatus?.isHealthy || printers.length === 0) return;
    if (probedSignatureRef.current === printersSignature) return;

    probedSignatureRef.current = printersSignature;
    void runProbe(printers);
  }, [serviceStatus?.isHealthy, printers, printersSignature, runProbe]);

  const setChannelPrinter = useCallback((channel: PrintChannel, printerId: string | null) => {
    setAssignments(previous => {
      const next = { ...previous, [channel]: printerId || null };
      localStorage.setItem(ASSIGNMENTS_STORAGE_KEY, serializeChannelAssignments(next));
      return next;
    });
  }, []);

  const saveCustomPrinter = useCallback((ip: string, port: number, name = 'Impresora Manual') => {
    const custom: PrintablePrinter = {
      id: CUSTOM_PRINTER_ID,
      name,
      ip,
      port,
      type: 'cashier',
    };

    setCustomPrinter(custom);
    localStorage.setItem(CUSTOM_PRINTER_STORAGE_KEY, JSON.stringify(custom));

    setAssignments(previous => {
      const next = { ...previous, caja: custom.id };
      localStorage.setItem(ASSIGNMENTS_STORAGE_KEY, serializeChannelAssignments(next));
      return next;
    });
  }, []);

  const persistChannels = useCallback(async (order: OrderComplete, channels: PrintingMetadata) => {
    if (!onSettledRef.current || Object.keys(channels).length === 0) return;

    try {
      await onSettledRef.current(order, channels);
    } catch {
      // Persistir el estado es best-effort: el ticket ya salió y no debe bloquear al cajero.
    }
  }, []);

  const runChannel = useCallback(
    async (order: OrderComplete, channel: PrintChannel, mode: 'auto' | 'retry'): Promise<ChannelState> => {
      updateChannel(channel, { status: 'loading', at: new Date().toISOString() });

      const startedAt = new Date().toISOString();
      const target = channelTargets[channel];

      try {
        if (channel === 'caja') {
          if (!target) throw new Error('No hay impresora de orden asignada');

          const response = await printUserTicket(toPrinterInfo(target), order);
          if (!response.ok) throw new Error(`La impresora respondió ${response.status}`);

          const state: ChannelState = { status: 'success', at: startedAt };
          updateChannel(channel, state);
          return state;
        }

        if (channel === 'cocina') {
          if (!target) {
            const state: ChannelState = {
              status: 'skipped',
              message: 'Sin impresora de cocina asignada',
              at: startedAt,
            };
            updateChannel(channel, state);
            return state;
          }

          const response = await printKitchenTicket(toPrinterInfo(target), order);
          if (!response.ok) throw new Error(`La impresora respondió ${response.status}`);

          const state: ChannelState = { status: 'success', at: startedAt };
          updateChannel(channel, state);
          return state;
        }

        // Canal fiscal (AFIP)
        if (!locationId) {
          const state: ChannelState = { status: 'skipped', message: 'Sin local para facturar', at: startedAt };
          updateChannel(channel, state);
          return state;
        }

        if (!(await hasAfipIntegration(locationId))) {
          const state: ChannelState = {
            status: 'skipped',
            message: 'Sin facturación AFIP configurada para este local',
            at: startedAt,
          };
          updateChannel(channel, state);
          return state;
        }

        // En reintentos se reutiliza el CAE ya emitido para no duplicar el comprobante.
        const afipData =
          mode === 'retry'
            ? ((await fetchExistingAfipOrderData(locationId, order.id)) ?? (await fetchAfipData(locationId, order.id)))
            : await fetchAfipData(locationId, order.id);

        if (!afipData) throw new Error('No se pudo obtener el CAE de AFIP');
        if (!target) throw new Error('No hay impresora asignada para la factura');

        const response = await printFiscalTicket(toPrinterInfo(target), {
          order,
          location: {
            streetname: location?.streetname ?? 'Sin calle',
            streetnumber: location?.streetnumber ?? 'Sin número',
            cityname: location?.cityname ?? 'Sin ciudad',
          },
          afipIntegration: afipData.integration,
          afipFECAEResponse: afipData.fecae,
        });
        if (!response.ok) throw new Error(`La impresora respondió ${response.status}`);

        const state: ChannelState = { status: 'success', at: startedAt };
        updateChannel(channel, state);
        return state;
      } catch (error) {
        const state: ChannelState = { status: 'error', message: errorMessage(error), at: startedAt };
        updateChannel(channel, state);
        return state;
      }
    },
    [channelTargets, locationId, location, updateChannel]
  );

  /**
   * Ejecuta canales de a uno (nunca en paralelo) con un reintento automático de los que fallaron.
   * No encola: se llama desde una tarea ya encolada.
   */
  const runChannels = useCallback(
    async (order: OrderComplete, channels: PrintChannel[], mode: 'auto' | 'retry') => {
      if (channels.length === 0) return;

      setIsBatchRunning(true);

      try {
        if (activeOrderIdRef.current !== order.id) {
          activeOrderIdRef.current = order.id;
          setActiveOrderId(order.id);
          channelStatesRef.current = INITIAL_CHANNEL_STATES;
          setChannelStates(INITIAL_CHANNEL_STATES);
        }

        const results = new Map<PrintChannel, ChannelState>();

        for (const channel of channels) {
          results.set(channel, await runChannel(order, channel, mode));
        }

        const failed = channels.filter(channel => results.get(channel)?.status === 'error');

        if (failed.length > 0) {
          await delay(AUTO_RETRY_DELAY_MS);

          for (const channel of failed) {
            results.set(channel, await runChannel(order, channel, 'retry'));
          }
        }

        const states: ChannelStates = { ...INITIAL_CHANNEL_STATES };
        for (const channel of channels) {
          states[channel] = results.get(channel) ?? INITIAL_CHANNEL_STATES[channel];
        }

        await persistChannels(order, toPersistedChannels(states, channels));
      } finally {
        setIsBatchRunning(false);
      }
    },
    [runChannel, persistChannels]
  );

  /** Serializa las tandas: una espera a que termine la anterior en vez de descartarse. */
  const enqueue = useCallback(<T,>(task: () => Promise<T>): Promise<T> => {
    const next = batchQueueRef.current.then(task, task);
    batchQueueRef.current = next.then(
      () => undefined,
      () => undefined
    );
    return next;
  }, []);

  const printOrder = useCallback(
    (order: OrderComplete, options?: PrintOrderOptions) =>
      enqueue(async () => {
        const persisted = parsePrintingMetadata(order.metadata);
        const candidates = options?.channels ?? PRINT_CHANNELS;
        const isNewOrder = activeOrderIdRef.current !== order.id;

        // En la misma orden el auto-print solo corre canales que nunca se intentaron
        // (evita duplicar la comanda adelantada o reintentar un error sin que el cajero decida).
        const channels = candidates.filter(channel => {
          if (!shouldRunChannel(persisted, channel)) return false;
          if (isNewOrder) return true;
          return channelStatesRef.current[channel].status === 'idle';
        });

        await runChannels(order, channels, 'auto');
      }),
    [enqueue, runChannels]
  );

  const retryChannel = useCallback(
    (order: OrderComplete, channel: PrintChannel) => enqueue(() => runChannels(order, [channel], 'retry')),
    [enqueue, runChannels]
  );

  const printAll = useCallback(
    (order: OrderComplete) =>
      enqueue(async () => {
        const persisted = parsePrintingMetadata(order.metadata);
        const isCurrentOrder = activeOrderIdRef.current === order.id;

        const channels = PRINT_CHANNELS.filter(channel => {
          if (!shouldRunChannel(persisted, channel)) return false;
          if (!isCurrentOrder) return true;

          const local = channelStatesRef.current[channel].status;
          return local !== 'success' && local !== 'skipped' && local !== 'loading';
        });

        await runChannels(order, channels, 'retry');
      }),
    [enqueue, runChannels]
  );

  const state = useMemo<PrintingState>(
    () => ({
      serviceStatus,
      printers,
      assignments,
      channelTargets,
      activeOrderId,
      channelStates,
      isPrinting: isBatchRunning || isPrintingChannels(channelStates),
      reachability,
      isProbing,
      probeState,
      hasReachablePrinter: reachableIps.length > 0,
      isLoadingPrinters: printersQuery.isLoading,
    }),
    [
      serviceStatus,
      printers,
      assignments,
      channelTargets,
      activeOrderId,
      channelStates,
      isBatchRunning,
      reachability,
      isProbing,
      probeState,
      reachableIps.length,
      printersQuery.isLoading,
    ]
  );

  const actions = useMemo<PrintingActions>(
    () => ({
      setChannelPrinter,
      saveCustomPrinter,
      probeNetwork,
      refreshService: () => void refetchService(),
      printOrder,
      retryChannel,
      printAll,
    }),
    [setChannelPrinter, saveCustomPrinter, probeNetwork, refetchService, printOrder, retryChannel, printAll]
  );

  return (
    <PrintingActionsCtx.Provider value={actions}>
      <PrintingStateCtx.Provider value={state}>{children}</PrintingStateCtx.Provider>
    </PrintingActionsCtx.Provider>
  );
}
